import React, { useState, useEffect, useRef } from 'react';
import './SettingsSection.css';

// ─── Avatares ────────────────────────────────────────────────────────────────
const avatarMap = {
    boy:   '/boy.jpeg',
    girl:  '/girl.jpeg',
    man:   '/man.jpeg',
    woman: '/woman.jpeg',
    none:  '/Prefiero-light.jpg',
};

const avatarOptions = [
    { value: 'man',   label: 'Man' },
    { value: 'woman', label: 'Woman' },
    { value: 'boy',   label: 'Boy' },
    { value: 'girl',  label: 'Girl' },
    { value: 'none',  label: 'Prefiero no decirlo' },
];

// ─── Alias status config ──────────────────────────────────────────────────────
const ALIAS_STATUS = {
    idle:      { color: 'transparent',                 icon: '',   text: '' },
    yours:     { color: 'var(--text-secondary)',        icon: '👤', text: 'Tu alias actual' },
    checking:  { color: '#6366f1',                     icon: '⏳', text: 'Verificando disponibilidad…' },
    available: { color: '#10b981',                     icon: '✅', text: 'Alias disponible' },
    taken:     { color: '#ef4444',                     icon: '✗',  text: 'Alias no disponible' },
};

// ─── Inline Toast ─────────────────────────────────────────────────────────────
function InlineToast({ toast }) {
    if (!toast) return null;
    const isOk = toast.type === 'ok';
    return (
        <div style={{
            padding: '10px 16px',
            borderRadius: 10,
            marginTop: 12,
            background: isOk ? 'rgba(16,185,129,.10)' : 'rgba(239,68,68,.10)',
            border: `1px solid ${isOk ? 'rgba(16,185,129,.30)' : 'rgba(239,68,68,.30)'}`,
            color: isOk ? '#059669' : '#dc2626',
            fontSize: '.85rem',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
        }}>
            {isOk ? '🎉' : '⚠️'} {toast.msg}
        </div>
    );
}

// ─── Componente principal ─────────────────────────────────────────────────────
function SettingsSection({ onProfileUpdate }) {
    const [profile,         setProfile]         = useState(null);
    const [formData,        setFormData]         = useState({ full_name: '', alias: '', password: '', confirm_password: '' });
    const [selectedAvatar,  setSelectedAvatar]   = useState('none');
    const [isLoading,       setIsLoading]        = useState(true);
    const [isSaving,        setIsSaving]         = useState(false);
    const [toast,           setToast]            = useState(null);
    const [aliasStatus,     setAliasStatus]      = useState('idle');
    const [aliasSuggestions, setAliasSuggestions] = useState([]);
    const debounceRef = useRef(null);

    const showToast = (msg, type = 'ok') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 5000);
    };

    // Carga inicial del perfil
    useEffect(() => {
        const token = localStorage.getItem('token');
        fetch('/api/profile/me', { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json())
            .then(data => {
                setProfile(data);
                setFormData({ full_name: data.full_name || '', alias: data.alias || '', password: '', confirm_password: '' });
                setSelectedAvatar(data.avatar_type || 'none');
                setAliasStatus('yours');
                setIsLoading(false);
            })
            .catch(() => setIsLoading(false));
    }, []);

    // Validación de alias en tiempo real (debounced 600ms)
    const checkAlias = (value) => {
        clearTimeout(debounceRef.current);
        const trimmed = value.trim();

        if (!trimmed) { setAliasStatus('idle'); setAliasSuggestions([]); return; }
        if (trimmed.toLowerCase() === (profile?.alias || '').toLowerCase()) {
            setAliasStatus('yours'); setAliasSuggestions([]); return;
        }

        setAliasStatus('checking');
        debounceRef.current = setTimeout(async () => {
            try {
                const token = localStorage.getItem('token');
                const res  = await fetch(
                    `/api/profile/alias-check?alias=${encodeURIComponent(trimmed)}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const data = await res.json();
                setAliasStatus(data.available ? 'available' : 'taken');
                setAliasSuggestions(data.available ? [] : (data.suggestions || []));
            } catch {
                setAliasStatus('idle');
            }
        }, 600);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'alias') checkAlias(value);
    };

    const applySuggestion = (s) => {
        setFormData(prev => ({ ...prev, alias: s }));
        setAliasSuggestions([]);
        setAliasStatus('available');
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (formData.password && formData.password !== formData.confirm_password) {
            showToast('Las contraseñas no coinciden.', 'err');
            return;
        }
        if (formData.alias.trim().length < 2) {
            showToast('El alias debe tener al menos 2 caracteres.', 'err');
            return;
        }
        if (aliasStatus === 'taken') {
            showToast('El alias ya está en uso. Elige otro o usa una sugerencia.', 'err');
            return;
        }
        if (aliasStatus === 'checking') {
            showToast('Espera a que termine la verificación del alias.', 'err');
            return;
        }

        setIsSaving(true);
        const payload = {
            full_name:   formData.full_name,
            alias:       formData.alias.trim(),
            avatar_type: selectedAvatar,
        };
        if (formData.password) payload.password = formData.password;

        try {
            const token = localStorage.getItem('token');
            const res  = await fetch('/api/profile/me', {
                method:  'PUT',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body:    JSON.stringify(payload),
            });
            const data = await res.json();

            if (!res.ok) {
                const detail = data.detail;
                const msg = typeof detail === 'object' ? detail.message : (detail || 'Error al guardar.');
                if (typeof detail === 'object' && detail.suggestions) {
                    setAliasSuggestions(detail.suggestions);
                    setAliasStatus('taken');
                }
                showToast(msg, 'err');
            } else {
                setProfile(data);
                setFormData(prev => ({ ...prev, password: '', confirm_password: '' }));
                setAliasStatus('yours');
                showToast('¡Perfil actualizado con éxito!');
                if (onProfileUpdate) onProfileUpdate();
            }
        } catch {
            showToast('Error de conexión. Intenta de nuevo.', 'err');
        } finally {
            setIsSaving(false);
        }
    };

    // ─── Estados de carga ──────────────────────────────────────────────────────
    if (isLoading) {
        return <div className="ss-card dp-neumorphic" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>⏳ Cargando configuración…</div>;
    }

    const joinDate = (profile?.created_at)
        ? new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric' }).format(new Date(profile.created_at))
        : 'Recientemente';

    const previewImage  = avatarMap[selectedAvatar] || avatarMap.none;
    const aliasInfo     = ALIAS_STATUS[aliasStatus] || ALIAS_STATUS.idle;

    return (
        <div className="ss-card dp-neumorphic">

            {/* Header con avatar actual */}
            <div className="ss-header">
                <img src={previewImage} alt="User Avatar" className="ss-avatar-img" />
                <div className="ss-info">
                    <h2>{profile?.full_name || 'Usuario'}</h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '.85rem' }}>{profile?.email}</p>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '.8rem' }}>Estudiante desde: {joinDate}</p>
                </div>
            </div>

            <form onSubmit={handleFormSubmit}>

                {/* ── Selector de Avatar ─────────────────────────────────── */}
                <div className="ss-avatar-selector">
                    <label className="ss-avatar-selector-label">Elige tu Avatar</label>
                    <div className="ss-avatar-options-group">
                        {avatarOptions.map(opt => (
                            <label key={opt.value} className="ss-avatar-label">
                                <input
                                    type="radio"
                                    name="avatar"
                                    value={opt.value}
                                    checked={selectedAvatar === opt.value}
                                    onChange={e => setSelectedAvatar(e.target.value)}
                                />
                                <span className="ss-radio-dot" />
                                <span className="ss-radio-text">{opt.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* ── Nombre completo ───────────────────────────────────── */}
                <div className="ss-form-group">
                    <label className="ss-form-label">Nombre Completo</label>
                    <input
                        name="full_name"
                        type="text"
                        className="ss-input"
                        value={formData.full_name}
                        onChange={handleInputChange}
                        maxLength={80}
                    />
                </div>

                {/* ── Alias con validación en tiempo real ───────────────── */}
                <div className="ss-form-group">
                    <label className="ss-form-label">
                        Alias
                        {aliasInfo.text && (
                            <span style={{ marginLeft: 8, fontSize: '.78rem', color: aliasInfo.color, fontWeight: 600 }}>
                                {aliasInfo.icon} {aliasInfo.text}
                            </span>
                        )}
                    </label>
                    <input
                        name="alias"
                        type="text"
                        className="ss-input"
                        value={formData.alias}
                        onChange={handleInputChange}
                        maxLength={20}
                        minLength={2}
                        pattern="[A-Za-z0-9_\-]+"
                        title="Solo letras, números, guion o guion bajo. Sin espacios."
                        style={{
                            borderColor: aliasStatus === 'available' ? '#10b981'
                                       : aliasStatus === 'taken'     ? '#ef4444'
                                       : undefined,
                        }}
                    />
                    <p style={{ margin: '4px 0 0', fontSize: '.73rem', color: 'var(--text-secondary)' }}>
                        Solo letras, números, _ o -. Máx. 20 caracteres. Único en la plataforma.
                    </p>

                    {/* Sugerencias cuando el alias está tomado */}
                    {aliasStatus === 'taken' && aliasSuggestions.length > 0 && (
                        <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '.75rem', color: 'var(--text-secondary)', alignSelf: 'center' }}>Disponibles:</span>
                            {aliasSuggestions.map(s => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => applySuggestion(s)}
                                    style={{
                                        padding: '3px 10px',
                                        borderRadius: 20,
                                        border: '1px solid #6366f1',
                                        background: 'rgba(99,102,241,.08)',
                                        color: '#6366f1',
                                        fontSize: '.78rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                    }}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Email (solo lectura) ──────────────────────────────── */}
                <div className="ss-form-group">
                    <label className="ss-form-label">
                        Correo Electrónico
                        <span style={{ marginLeft: 6, fontSize: '.73rem', color: 'var(--text-secondary)' }}>
                            (no editable — contacta soporte para cambiarlo)
                        </span>
                    </label>
                    <input
                        maxLength={120}
                        type="email"
                        className="ss-input"
                        value={profile?.email || ''}
                        readOnly
                        disabled
                        style={{ opacity: .6, cursor: 'not-allowed' }}
                    />
                </div>

                {/* ── Nueva contraseña ──────────────────────────────────── */}
                <div className="ss-form-group">
                    <label className="ss-form-label">Nueva Contraseña</label>
                    <input
                        name="password"
                        type="password"
                        className="ss-input"
                        placeholder="Dejar en blanco para no cambiar"
                        value={formData.password}
                        onChange={handleInputChange}
                        minLength={formData.password ? 8 : undefined}
                    />
                </div>
                <div className="ss-form-group">
                    <label className="ss-form-label">Confirmar Contraseña</label>
                    <input
                        name="confirm_password"
                        type="password"
                        className="ss-input"
                        placeholder="Repetir nueva contraseña"
                        value={formData.confirm_password}
                        onChange={handleInputChange}
                        style={{
                            borderColor: formData.confirm_password && formData.password !== formData.confirm_password
                                ? '#ef4444' : undefined,
                        }}
                    />
                    {formData.confirm_password && formData.password !== formData.confirm_password && (
                        <p style={{ margin: '4px 0 0', fontSize: '.73rem', color: '#ef4444' }}>
                            ✗ Las contraseñas no coinciden
                        </p>
                    )}
                </div>

                {/* ── Feedback inline ───────────────────────────────────── */}
                <InlineToast toast={toast} />

                {/* ── Botón guardar ─────────────────────────────────────── */}
                <button
                    type="submit"
                    className="ss-btn"
                    disabled={isSaving || aliasStatus === 'checking' || aliasStatus === 'taken'}
                    style={{ marginTop: 16, opacity: isSaving ? .7 : 1 }}
                >
                    {isSaving ? '⏳ Guardando…' : '💾 Guardar Cambios'}
                </button>

            </form>
        </div>
    );
}

export default SettingsSection;